/* eslint-disable no-unused-vars */
import React, { useMemo } from 'react';
import {
  View,
  Text,
  SafeAreaView,
  StatusBar,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Linking,
  Dimensions,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useVideoPlayer, VideoView } from 'expo-video';

const { width } = Dimensions.get('window');

// ── Render-safe date helper (self-contained; mirrors app/utils/format.js) ──────
const fmtDate = (d) => {
  if (!d) return 'TBA';
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return 'TBA';
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const isUrl = (s) => typeof s === 'string' && /^https?:\/\//i.test(s.trim());

const SessionRecordingScreen = ({ route, navigation }) => {
  const course = route.params?.course || {};
  const color = course.color || '#0e4e96';

  const recordingUrl = course.recordingUrl || null;
  const topics = Array.isArray(course.topics) ? course.topics : [];
  const materials = Array.isArray(course.materials) ? course.materials : [];

  // expo-video player. Hook must run unconditionally; a null source is fine.
  const player = useVideoPlayer(recordingUrl, (p) => {
    if (p) { p.loop = false; }
  });

  const openExternal = () => {
    if (!recordingUrl) return;
    Linking.openURL(recordingUrl).catch(() =>
      Alert.alert('Unable to open', 'Could not open the recording link.')
    );
  };

  const openMaterial = (m) => {
    if (isUrl(m)) {
      Linking.openURL(m).catch(() => Alert.alert('Unable to open', 'Could not open this file.'));
    } else {
      Alert.alert('Course Material', `"${m}" will be available to download from the session resources.`);
    }
  };

  // Friendly empty-state copy when there's no recording URL yet.
  const emptyCopy = useMemo(() => {
    const status = String(course.status || '').toLowerCase();
    if (status === 'upcoming' || status === 'live now') {
      return 'The recording will be available here shortly after the live session ends.';
    }
    return 'This recording is still being processed and will appear here soon.';
  }, [course.status]);

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor={color} />

      {/* Header */}
      <LinearGradient colors={[color, `${color}DD`]} style={styles.header}>
        <View style={styles.headerTop}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.iconBtn}>
            <Ionicons name="arrow-back" size={26} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.headerTag}>📼 RECORDING</Text>
          <View style={{ width: 26 }} />
        </View>
        <Text style={styles.headerTitle} numberOfLines={2}>{course.title || 'Session Recording'}</Text>
        {!!course.instructor && (
          <Text style={styles.headerInstructor} numberOfLines={1}>with {course.instructor}</Text>
        )}
      </LinearGradient>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Player or empty state */}
        {recordingUrl ? (
          <View style={styles.playerCard}>
            <VideoView
              player={player}
              style={styles.player}
              contentFit="contain"
              allowsFullscreen
              allowsPictureInPicture
              nativeControls
            />
            <TouchableOpacity style={styles.externalBtn} onPress={openExternal}>
              <MaterialCommunityIcons name="open-in-new" size={16} color="#0e4e96" />
              <Text style={styles.externalBtnText}>Open in browser</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.emptyCard}>
            <MaterialCommunityIcons name="video-off-outline" size={48} color="#94a3b8" />
            <Text style={styles.emptyTitle}>Recording not available yet</Text>
            <Text style={styles.emptyText}>{emptyCopy}</Text>
          </View>
        )}

        {/* Meta row */}
        <View style={styles.metaRow}>
          <View style={styles.metaItem}>
            <MaterialCommunityIcons name="calendar" size={18} color="#64748B" />
            <Text style={styles.metaText}>{fmtDate(course.date)}</Text>
          </View>
          <View style={styles.metaItem}>
            <MaterialCommunityIcons name="timer" size={18} color="#64748B" />
            <Text style={styles.metaText}>{course.duration || '—'}</Text>
          </View>
          {!!course.rating && (
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="star" size={18} color="#f59e0b" />
              <Text style={styles.metaText}>{course.rating}</Text>
            </View>
          )}
        </View>

        {/* Description */}
        {!!course.description && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>About this session</Text>
            <Text style={styles.description}>{course.description}</Text>
          </View>
        )}

        {/* Topics */}
        {topics.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>What's covered</Text>
            {topics.map((t, i) => (
              <View key={i} style={styles.bulletRow}>
                <MaterialCommunityIcons name="check-circle" size={16} color="#4CAF50" />
                <Text style={styles.bulletText}>{t}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Materials */}
        {materials.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Resources & materials</Text>
            {materials.map((m, i) => (
              <TouchableOpacity key={i} style={styles.materialRow} onPress={() => openMaterial(m)}>
                <MaterialCommunityIcons name="file-download-outline" size={20} color="#0e4e96" />
                <Text style={styles.materialText} numberOfLines={1}>{m}</Text>
                <Ionicons name="chevron-forward" size={18} color="#94a3b8" />
              </TouchableOpacity>
            ))}
          </View>
        )}

        <View style={{ height: 28 }} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#f0f4f8' },
  header: { paddingTop: 16, paddingBottom: 22, paddingHorizontal: 20 },
  headerTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  iconBtn: { padding: 4 },
  headerTag: { color: '#fff', fontSize: 12, fontWeight: 'bold', opacity: 0.9, letterSpacing: 1 },
  headerTitle: { fontSize: 22, fontWeight: 'bold', color: '#fff', marginBottom: 4 },
  headerInstructor: { fontSize: 14, color: '#fff', opacity: 0.85 },
  content: { flex: 1 },

  playerCard: {
    backgroundColor: '#000', marginHorizontal: 15, marginTop: -10, borderRadius: 14, overflow: 'hidden',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.15, shadowRadius: 8, elevation: 5,
  },
  player: { width: '100%', height: (width - 30) * 0.5625, backgroundColor: '#000' }, // 16:9
  externalBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
    backgroundColor: '#fff', paddingVertical: 12,
  },
  externalBtnText: { color: '#0e4e96', fontSize: 14, fontWeight: '600', marginLeft: 6 },

  emptyCard: {
    backgroundColor: '#fff', marginHorizontal: 15, marginTop: -10, borderRadius: 14, padding: 28, alignItems: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 6, elevation: 4,
  },
  emptyTitle: { fontSize: 16, fontWeight: 'bold', color: '#1f2937', marginTop: 12 },
  emptyText: { fontSize: 14, color: '#64748B', textAlign: 'center', marginTop: 6, lineHeight: 20 },

  metaRow: {
    flexDirection: 'row', justifyContent: 'space-around', backgroundColor: '#fff',
    marginHorizontal: 15, marginTop: 14, borderRadius: 12, paddingVertical: 14,
  },
  metaItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  metaText: { fontSize: 13, color: '#1f2937', fontWeight: '600', marginLeft: 6 },

  section: { backgroundColor: '#fff', marginHorizontal: 15, marginTop: 14, borderRadius: 14, padding: 18 },
  sectionTitle: { fontSize: 16, fontWeight: 'bold', color: '#1f2937', marginBottom: 12 },
  description: { fontSize: 15, color: '#4b5563', lineHeight: 22 },

  bulletRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  bulletText: { fontSize: 14, color: '#4b5563', marginLeft: 10, flex: 1 },

  materialRow: {
    flexDirection: 'row', alignItems: 'center', paddingVertical: 12,
    borderBottomWidth: 1, borderBottomColor: '#f1f5f9',
  },
  materialText: { fontSize: 14, color: '#1f2937', marginLeft: 12, flex: 1 },
});

export default SessionRecordingScreen;
